import { offlineDB } from "@/lib/offlineDb"
import { getAccessToken } from "@/lib/api"
import { debug, error as logError, warn } from "@/utils/debug"
import { encrypt, decrypt } from "@/lib/crypto"

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
export const isOnline = () => navigator.onLine

// State machine for queue replay to prevent race conditions
enum ReplayState {
  IDLE = "idle",
  REPLAYING = "replaying",
  FAILED = "failed",
}

let replayState = ReplayState.IDLE

// Maximum retry attempts before giving up on a queued item
const MAX_RETRY_ATTEMPTS = 5

function toPlainJSON<T>(v: T): T {
  return JSON.parse(JSON.stringify(v ?? null)) as T
}

export async function addQueueWithTempId(action: string, input: unknown, tempId: string) {
  // SECURITY: Encrypt sensitive data before storing in IndexedDB
  const token = await getAccessToken()
  const encryptedInput = token ? await encrypt(toPlainJSON(input), token) : toPlainJSON(input)

  await offlineDB.functionCalls.add({
    action,
    input: encryptedInput,
    createdAt: Date.now(),
    tempId,
  })
}

export async function deleteQueuedByTempId(tempId: string) {
  const rec = await offlineDB.functionCalls.where("tempId").equals(tempId).first()
  if (rec) await offlineDB.functionCalls.delete(rec.id!)
}

export async function executeOrQueueFunction<T = unknown>(
  action: string,
  input?: unknown,
): Promise<{ data: T | null; error: unknown } | null> {
  if (!isOnline()) {
    // SECURITY: Encrypt sensitive data before storing in IndexedDB
    const token = await getAccessToken()
    const encryptedInput = token ? await encrypt(toPlainJSON(input), token) : toPlainJSON(input)

    // generic path: no tempId info, so callers who need it should use addQueueWithTempId
    await offlineDB.functionCalls.add({
      action,
      input: encryptedInput,
      createdAt: Date.now(),
    })
    return null
  }
  const token = await getAccessToken()
  try {
    const res = await fetch(`${supabaseUrl}/functions/v1/api`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ action, input }),
    })
    let json: unknown = null
    try {
      json = await res.json()
    } catch {
      json = null
    }
    if (
      !res.ok ||
      (json !== null && typeof json === "object" && "ok" in json && json.ok === false)
    ) {
      if (res.status === 0) {
        // SECURITY: Encrypt sensitive data before storing
        const encryptedInput = token ? await encrypt(toPlainJSON(input), token) : toPlainJSON(input)
        await offlineDB.functionCalls.add({
          action,
          input: encryptedInput,
          createdAt: Date.now(),
        })
        return null
      }
      const err =
        json !== null &&
        typeof json === "object" &&
        "error" in json &&
        typeof json.error === "string"
          ? { message: json.error, status: res.status }
          : json !== null && typeof json === "object" && "error" in json
            ? { ...(json.error as Record<string, unknown>), status: res.status }
            : { message: "Unknown error", status: res.status }
      return { data: null, error: err }
    }
    return {
      data: json !== null && typeof json === "object" && "data" in json ? (json.data as T) : null,
      error: null,
    }
  } catch {
    // SECURITY: Encrypt sensitive data before storing
    const encryptedInput = token ? await encrypt(toPlainJSON(input), token) : toPlainJSON(input)
    await offlineDB.functionCalls.add({
      action,
      input: encryptedInput,
      createdAt: Date.now(),
    })
    return null
  }
}

/**
 * Replay queued function calls when online.
 * Uses a state machine to prevent concurrent replays and properly handles errors.
 *
 * - 4xx errors (client errors): Remove from queue immediately (permanent failure)
 * - 5xx errors (server errors): Keep in queue and retry later (temporary failure)
 * - Network errors: Keep in queue and retry later
 * - Max retries exceeded: Remove from queue and log error
 */
export async function replayQueuedFunctionCalls() {
  // Prevent concurrent replay attempts
  if (replayState === ReplayState.REPLAYING) {
    debug("[Queue] Replay already in progress, skipping")
    return
  }

  // Don't replay if offline
  if (!isOnline()) {
    debug("[Queue] Offline, skipping replay")
    return
  }

  replayState = ReplayState.REPLAYING
  let successCount = 0
  let errorCount = 0
  let skippedCount = 0

  try {
    const token = await getAccessToken()
    const list = await offlineDB.functionCalls.orderBy("createdAt").toArray()

    if (list.length === 0) {
      debug("[Queue] No queued items to replay")
      replayState = ReplayState.IDLE
      return
    }

    debug(`[Queue] Replaying ${list.length} queued function calls`)

    for (const rec of list) {
      const retryCount = rec.retryCount ?? 0

      // Check if max retries exceeded
      if (retryCount >= MAX_RETRY_ATTEMPTS) {
        logError(
          `[Queue] Max retries (${MAX_RETRY_ATTEMPTS}) exceeded for ${rec.action}, removing from queue`,
          { rec },
        )
        await offlineDB.functionCalls.delete(rec.id!)
        skippedCount++
        continue // Don't break - continue with next item
      }

      try {
        // SECURITY: Decrypt input before sending (if encrypted)
        let decryptedInput = rec.input
        if (token && typeof rec.input === "string") {
          try {
            decryptedInput = await decrypt(rec.input, token)
          } catch (decryptError) {
            // If decryption fails, data might not be encrypted (legacy data)
            debug(`[Queue] Using unencrypted input for ${rec.action}`)
            decryptedInput = rec.input
          }
        }

        const res = await fetch(`${supabaseUrl}/functions/v1/api`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ action: rec.action, input: decryptedInput }),
        })

        // Distinguish between permanent and temporary failures
        if (!res.ok) {
          // 4xx errors = client errors (permanent failures)
          if (res.status >= 400 && res.status < 500) {
            logError(
              `[Queue] Permanent failure (${res.status}) for ${rec.action}, removing from queue`,
              { rec },
            )
            await offlineDB.functionCalls.delete(rec.id!)
            errorCount++
            continue // Don't break - continue with next item
          }

          // 5xx errors = server errors (temporary failures)
          if (res.status >= 500) {
            warn(`[Queue] Temporary failure (${res.status}) for ${rec.action}, will retry later`, {
              rec,
              retryCount,
            })
            await offlineDB.functionCalls.update(rec.id!, {
              retryCount: retryCount + 1,
              lastError: `HTTP ${res.status}`,
            })
            errorCount++
            continue // Don't break - continue with next item
          }

          // Other non-ok responses (unexpected)
          warn(`[Queue] Unexpected status (${res.status}) for ${rec.action}, will retry later`, {
            rec,
          })
          await offlineDB.functionCalls.update(rec.id!, {
            retryCount: retryCount + 1,
            lastError: `HTTP ${res.status}`,
          })
          errorCount++
          continue // Don't break - continue with next item
        }

        // Success! Dispatch event and remove from queue
        try {
          const payload = await res.json()
          window.dispatchEvent(
            new CustomEvent("offline-replay:success", {
              detail: { action: rec.action, tempId: rec.tempId, payload },
            }),
          )
        } catch (jsonError) {
          // Ignore JSON parse errors - some endpoints don't return JSON
          debug(`[Queue] No JSON response for ${rec.action} (this is okay)`)
        }

        await offlineDB.functionCalls.delete(rec.id!)
        successCount++
        debug(`[Queue] Successfully replayed ${rec.action}`)
      } catch (error) {
        // Network error or other exception
        warn(`[Queue] Network error for ${rec.action}, will retry later`, {
          error,
          rec,
          retryCount,
        })
        await offlineDB.functionCalls.update(rec.id!, {
          retryCount: retryCount + 1,
          lastError: error instanceof Error ? error.message : "Network error",
        })
        errorCount++
      }
    }

    debug(
      `[Queue] Replay complete: ${successCount} succeeded, ${errorCount} failed, ${skippedCount} skipped`,
    )
    replayState = ReplayState.IDLE
  } catch (error) {
    logError("[Queue] Fatal error during replay", error)
    replayState = ReplayState.FAILED
    throw error
  }
}
